/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { KeyboardArrowDown } from "@mui/icons-material";
import { Alert, MenuItem, Select, Stack } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { FC, type JSX } from "react";
import { Control, Controller } from "react-hook-form";

import { DatePicker } from "../../../../components/DateRangePicker";
import FormControlLabel from "../../../../components/form-controls/FormControlLabel/FormControlLabel";
import InputFormControl from "../../../../components/form-controls/InputFormControl/InputFormControl";
import { ISelectOption } from "../../../../components/form-controls/SelectFormControl/SelectFormControl";
import TestIds from "../../../../testIds";
import { getStringFromArray } from "../../../../util/formatting";
import {
  ActivityStatus,
  ActivityType,
  ContactPerson,
  ContactPersonStatus,
} from "../../api/types/types";
import StatusBadge from "../StatusBadge/StatusBadge";

import { CreateOrEditFormValues } from "./CreateOrUpdateActivityModal";
import { StyledActivitiesSelect } from "./StyledActivitiesSelect";

export interface CreateOrUpdateActivityModalStep1Props {
  control: Control<CreateOrEditFormValues>;
  contactPersons: ContactPerson[];
  owner: string;
  disableActivityTypeSelect?: boolean;
  error?: string;
}

const activityTypeOptions = [
  { value: ActivityType.Phone, label: "Phone Call" },
  { value: ActivityType.Appointment, label: "Appointment" },
];

const CreateOrUpdateActivityModalStep1: FC<CreateOrUpdateActivityModalStep1Props> = ({
  control,
  contactPersons,
  owner,
  disableActivityTypeSelect,
  error,
}): JSX.Element => {
  const contactPersonOptions: ISelectOption[] = contactPersons
    .filter((contact: ContactPerson) => contact.status === ContactPersonStatus.Active)
    .map((contact: ContactPerson) => ({
      value: contact.contactId,
      label: getStringFromArray([contact.firstName, contact.lastName]),
    }));

  const validateTextField: (value?: string) => boolean = (value) => {
    return !!value?.trim();
  };

  const validateDateField: (value?: Dayjs) => boolean = (value) => {
    return !!value && value.isValid();
  };

  return (
    <Stack gap={2}>
      <Stack direction={"column"}>
        <FormControlLabel required color="secondary">
          Status
        </FormControlLabel>
        <Controller
          name="activityStatus"
          control={control}
          rules={{
            validate: (value: ActivityStatus) => validateTextField(value?.toString()),
          }}
          render={({ field }) => (
            <Select
              {...field}
              sx={{
                height: "3rem",
                width: "16.25rem",
                "& .MuiSelect-icon": {
                  top: "calc(50% - .5em)",
                },
              }}
              IconComponent={KeyboardArrowDown}
              data-testid={TestIds.feat.activities.createOrEditActivityModal.activityStatus}
              required
            >
              <MenuItem value={ActivityStatus.Open}>
                <StatusBadge status={ActivityStatus.Open} />
              </MenuItem>
              <MenuItem value={ActivityStatus.Completed}>
                <StatusBadge status={ActivityStatus.Completed} />
              </MenuItem>
            </Select>
          )}
        />
      </Stack>

      <Controller
        name="activityType"
        control={control}
        rules={{
          validate: (value: ActivityType | string) => validateTextField(value?.toString()),
        }}
        render={({ field, fieldState: { error } }) => (
          <StyledActivitiesSelect
            farmlands-variant="farmlands2.0_default"
            {...field}
            labelColor="secondary"
            label="Activity Type"
            required
            options={activityTypeOptions}
            error={!!error}
            data-testid={TestIds.feat.activities.createOrEditActivityModal.activityType}
            placeholder="Select an Activity type"
            onChange={(e) => field.onChange(e.target.value)}
            disabled={disableActivityTypeSelect}
          />
        )}
      />

      <Controller
        name="subject"
        control={control}
        rules={{
          validate: (value: string) => validateTextField(value),
        }}
        render={({ field, fieldState: { error } }) => (
          <InputFormControl
            {...field}
            labelColor="secondary"
            farmlands-variant="farmlands2.0_default"
            label="Subject"
            required
            error={!!error}
            data-testid={TestIds.feat.activities.createOrEditActivityModal.subject}
            inputProps={{ maxLength: 200 }}
            placeholder="Enter an Activity subject..."
          />
        )}
      />

      <Controller
        name="description"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <InputFormControl
            {...field}
            labelColor="secondary"
            farmlands-variant="farmlands2.0_default"
            label="Description"
            multiline
            rows={4}
            inputProps={{ maxLength: 2000 }}
            error={!!error}
            data-testid={TestIds.feat.activities.createOrEditActivityModal.description}
            placeholder="Enter an Activity description..."
          />
        )}
      />

      <Stack
        direction={"column"}
        data-testid={TestIds.feat.activities.createOrEditActivityModal.scheduleDate}
      >
        <FormControlLabel color="secondary" required>
          Scheduled Date
        </FormControlLabel>
        <Controller
          name="scheduleDate"
          control={control}
          rules={{
            validate: (value: dayjs.Dayjs | undefined) => validateDateField(value),
          }}
          render={({ field, fieldState: { error } }) => (
            <DatePicker
              format="DD/MM/YYYY"
              required
              sx={{ width: 260 }}
              value={field.value ? dayjs(field.value) : undefined}
              setDate={(newValue) => {
                if (newValue) {
                  field.onChange(newValue.hour(8));
                }
              }}
              error={!!error}
              disableWeekends={false}
            />
          )}
        />
      </Stack>

      <StyledActivitiesSelect
        farmlands-variant="farmlands2.0_default"
        labelColor="secondary"
        label="Owner"
        required
        options={[{ value: owner, label: owner }]}
        value={owner}
        disabled={true}
        data-testid={TestIds.feat.activities.createOrEditActivityModal.owner}
      />

      <Controller
        name="contactId"
        control={control}
        rules={{
          validate: (value: string) => validateTextField(value),
        }}
        render={({ field, fieldState: { error } }) => (
          <StyledActivitiesSelect
            {...field}
            farmlands-variant="farmlands2.0_default"
            labelColor="secondary"
            label="Customer Contact"
            required
            options={contactPersonOptions}
            error={!!error}
            data-testid={TestIds.feat.activities.createOrEditActivityModal.contactId}
            placeholder="Select an Customer contact"
            onChange={(e) => field.onChange(e.target.value)}
          />
        )}
      />
      {error && (
        <Alert
          severity="error"
          data-testid={TestIds.feat.activities.createOrEditActivityModal.error}
        >
          {error}
        </Alert>
      )}
    </Stack>
  );
};

export default CreateOrUpdateActivityModalStep1;
